import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "lucide-react";

// --- Core Imports ---
import { useAuth } from "../../contexts/AuthContext"; // Import the useAuth hook

// --- Component Imports ---
import ProfileIcon from "../../components/ProfileIcon/ProfileIcon";
import IconButton from "../../components/IconButton/IconButton";
import ButtonWithIcon from "../../components/ButtonwithIcon/ButtonWithIcon";
import axiosInstance from "../../axiosInstance";

// --- Asset Imports ---
import profileicon from "../../Images/user-1.jpg";
import coverphoto from "../../Images/Designer.png";

// --- Styles ---
import "./Profile.scss";
import LoadingScreen from "../../components/LoadingScreen/LoadingScreen";

function Profile() {
  // Get user data and loading state from AuthContext
  const { user, isLoading: isAuthLoading } = useAuth();
  const navigate = useNavigate();

  // State for data fetched specifically on this page
  const [qualifications, setQualifications] = useState([]);
  const [certifications, setCertifications] = useState([]);
  const [socialMedia, setSocialMedia] = useState({});
  const [profileDetails, setProfileDetails] = useState(null);
  const [isFetchingDetails, setIsFetchingDetails] = useState(true);
  const [activeTab, setActiveTab] = useState("personal");

  useEffect(() => {
    // Fetch detailed data only if the user object is available
    if (user) {
      const fetchData = async () => {
        setIsFetchingDetails(true);
        try {
          const [profileRes, qualRes, certRes, socialRes] = await Promise.all([
            axiosInstance.get(
              `${process.env.REACT_APP_API_EMPLOYEE_URL}/employee/profile-Details`,
            ),
            axiosInstance.get(
              `${process.env.REACT_APP_API_EMPLOYEE_URL}/qualifications/All`,
            ),
            axiosInstance.get(
              `${process.env.REACT_APP_API_EMPLOYEE_URL}/certifications/All`,
            ),
            axiosInstance.get(
              `${process.env.REACT_APP_API_EMPLOYEE_URL}/social-media`,
            ),
          ]);

          setProfileDetails(profileRes.data.data);
          setQualifications(qualRes.data.data || []);
          setCertifications(certRes.data.data || []);
          setSocialMedia(socialRes.data.data || {});
        } catch (error) {
          console.error("Error fetching profile data:", error);
        } finally {
          setIsFetchingDetails(false);
        }
      };

      fetchData();
    }
  }, [user]); // Re-run this effect if the user object changes

  const formatEnum = (value) => {
    if (!value) return "";
    return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatRole = (role) => {
    if (!role) return "No Role Assigned";
    return role
      .replace("ROLE_", "")
      .toLowerCase()
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const getSocialLinks = () => {
    const links = [];
    if (socialMedia.twitterLink)
      links.push({
        name: "Twitter",
        url: socialMedia.twitterLink,
        icon: "mdi:twitter",
      });
    if (socialMedia.fbLink)
      links.push({
        name: "Facebook",
        url: socialMedia.fbLink,
        icon: "mdi:facebook",
      });
    if (socialMedia.instaLink)
      links.push({
        name: "Instagram",
        url: socialMedia.instaLink,
        icon: "mdi:instagram",
      });
    return links;
  };

  // Combine auth loading and detail fetching to get a complete loading state
  const isLoading = isAuthLoading || isFetchingDetails;

  if (isLoading) {
    return (
      <div className="profile-loading">
        <LoadingScreen />
      </div>
    );
  }

  // Handle case where user is not logged in or data failed to load
  if (!user) {
    return (
      <div className="profile-loading">
        <p>Could not load user profile. Please try logging in again.</p>
      </div>
    );
  }

  // Use user data from context for display
  const { employeeName, role, profileImage } = user;
  const displayImage = profileImage
    ? `data:image/png;base64,${profileImage}`
    : profileicon;
  const displayRole = profileDetails?.role || role;
  const displayAuthorities = profileDetails?.authorities || user.authorities;

  return (
    <div className="profile-section-container">
      {/* Header Section */}
      <div className="profile-header relative">
        <div className="cover-section">
          <img src={coverphoto} alt="Cover" className="cover-image" />
          <div className="cover-overlay"></div>
        </div>

        <div className="absolute top-4 right-4 z-10">
          <ButtonWithIcon
            onClick={() => navigate("/account-settings")}
            text="Edit Profile"
            className="edit-button"
            icon={"iconamoon:edit-light"}
          />
        </div>

        <div className="profile-info">
          <div className="profile-avatar">
            <ProfileIcon size="large" src={displayImage} alt={employeeName} />
          </div>

          <div className="profile-details">
            <h1 className="profile-name">{employeeName}</h1>
            <p className="profile-role">{formatRole(displayRole)}</p>

            {getSocialLinks().length > 0 && (
              <div className="social-links">
                {getSocialLinks().map((link) => (
                  <IconButton
                    key={link.name}
                    onClick={() => window.open(link.url, "_blank")}
                    className={`social-btn ${link.name.toLowerCase()}`}
                    title={link.name}
                  >
                    {/* Using a generic icon component for simplicity */}
                    <Icon name={link.icon.replace("mdi:", "")} size={20} />
                  </IconButton>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="profile-content">
        <div className="tab-navigation">
          <button
            className={`tab-btn ${activeTab === "personal" ? "active" : ""}`}
            onClick={() => setActiveTab("personal")}
          >
            Personal Info
          </button>
          <button
            className={`tab-btn ${activeTab === "work" ? "active" : ""}`}
            onClick={() => setActiveTab("work")}
          >
            Work Details
          </button>
          <button
            className={`tab-btn ${activeTab === "education" ? "active" : ""}`}
            onClick={() => setActiveTab("education")}
          >
            Education
          </button>
        </div>

        <div className="tab-content">
          {activeTab === "personal" && profileDetails && (
            <div className="info-grid">
              <div className="info-card">
                <h3>Contact Information</h3>
                <div className="info-item">
                  <span className="label">Email</span>
                  <span className="value">{profileDetails.email}</span>
                </div>
                <div className="info-item">
                  <span className="label">Company Email</span>
                  <span className="value">{profileDetails.companyEmail}</span>
                </div>
                <div className="info-item">
                  <span className="label">Mobile</span>
                  <span className="value">{profileDetails.mobileNo}</span>
                </div>
                <div className="info-item">
                  <span className="label">Emergency Contact</span>
                  <span className="value">
                    {profileDetails.emergencyContactName} (
                    {profileDetails.emergencyContactNo})
                  </span>
                </div>
              </div>
              <div className="info-card">
                <h3>Personal Details</h3>
                <div className="info-item">
                  <span className="label">Date of Birth</span>
                  <span className="value">
                    {formatDate(profileDetails.dob)}
                  </span>
                </div>
                <div className="info-item">
                  <span className="label">Blood Group</span>
                  <span className="value">{profileDetails.bloodGroup}</span>
                </div>
                <div className="info-item">
                  <span className="label">Gender</span>
                  <span className="value">{profileDetails.gender}</span>
                </div>
                <div className="info-item">
                  <span className="label">Marital Status</span>
                  <span className="value">{profileDetails.martialStatus}</span>
                </div>
              </div>
            </div>
          )}

          {activeTab === "work" && profileDetails && (
            <div className="info-grid">
              <div className="info-card">
                <h3>Work Information</h3>

                <div className="info-item">
                  <span className="label">Branch</span>
                  <span className="value">
                    {profileDetails.branch || "N/A"}
                  </span>
                </div>

                <div className="info-item">
                  <span className="label">Date of Joining</span>
                  <span className="value">
                    {formatDate(profileDetails.doj)}
                  </span>
                </div>
                <div className="info-item">
                  <span className="label">Employment Type</span>
                  <span className="value">
                    {profileDetails.employementType}
                  </span>
                </div>
                <div className="info-item">
                  <span className="label">Manager</span>
                  <span className="value">{profileDetails.managerName}</span>
                </div>
                <div className="info-item">
                  <span className="label">HR</span>
                  <span className="value">{profileDetails.hrName}</span>
                </div>
                <div className="info-item">
                  <span className="label">Role</span>
                  <span className="value">{formatRole(displayRole)}</span>
                </div>
                <div className="info-item">
                  <span className="label">Shift Timings</span>
                  <span className="value">
                    {profileDetails.officeTimings?.shiftFrom} -{" "}
                    {profileDetails.officeTimings?.shiftTo}
                  </span>
                </div>
              </div>
              <div className="info-card">
                <h3>Authorities</h3>
                <div className="authorities-list">
                  {displayAuthorities?.map((authority, index) => (
                    <span key={index} className="authority-tag">
                      {formatRole(authority)}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === "education" && (
            <div className="info-grid">
              <div className="info-card">
                <h3>Qualifications</h3>
                {qualifications.length > 0 ? (
                  <div className="education-list">
                    {qualifications.map((qual) => (
                      <div
                        key={formatEnum(qual.qualificationId)}
                        className="education-item"
                      >
                        <div className="education-degree">
                          {qual.degreeName}
                        </div>
                        <div className="education-institution">
                          {qual.institution}
                        </div>
                        <div className="education-percentage">
                          {qual.percentage}%
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="no-data">No qualifications available</p>
                )}
              </div>
              <div className="info-card">
                <h3>Certifications</h3>
                {certifications.length > 0 ? (
                  <div className="education-list">
                    {certifications.map((cert) => (
                      <div
                        key={cert.certificationId}
                        className="education-item"
                      >
                        <div className="education-degree">
                          {cert.certificationName}
                        </div>
                        <div className="education-institution">
                          {cert.institutionName}
                        </div>
                        <div className="education-date">
                          Certified: {formatDate(cert.certifiedOn)}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="no-data">No certifications available</p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Profile;
